import { useMemo } from "react";
import { differenceInCalendarDays, parseISO } from "date-fns";
import {
  useCurrentBudgetPeriod,
  useBudgetSpentInPeriod,
  type CompanyBudgetPeriod,
  type BudgetPeriodTotals,
} from "./useCurrentBudgetPeriod";

/**
 * MS558: burn-rate forecast hook for the Dashboard pool-status widget.
 *
 * Composes useCurrentBudgetPeriod + useBudgetSpentInPeriod and runs a
 * simple linear-projection forecast at read time. No caching, no cron;
 * the computation is cheap (a few arithmetic ops over already-fetched
 * totals) and the values flip on every refetch of the underlying
 * queries.
 *
 * The forecast model (per MS552 §6 + MS551 §6):
 *
 *   pacePerDay  = spent / max(1, daysIntoPeriod)
 *   projected   = pace * (daysIntoPeriod + daysRemaining)
 *               = pace * totalPeriodDays
 *   capacity    = allocated + carried_over + adjustments
 *   overrun     = max(0, projected − capacity)
 *
 * Linear projection (not 90-day rolling avg per MS551 §6) is the v1
 * choice. Reasoning: the widget already shows in-period spend; the
 * forecast that buyers can reason about is "if I keep going at the
 * same rate, where do I land?" — extrapolating from in-period burn is
 * the explanation that matches the headline number. The 90-day avg is
 * a better predictor for *new* periods (where there's no in-period
 * spend yet), but those land at warningLevel 'none' anyway via the
 * data-too-sparse guard.
 *
 * Warning levels — confidence threshold first, then severity tiers:
 *
 *   - 'none':     daysIntoPeriod < 3                         (too sparse)
 *                 OR period ended                            (no signal)
 *                 OR projected ≤ 90% of capacity             (on pace)
 *                 OR spent = 0                               (no signal)
 *   - 'caution':  projected 90–100% of capacity              (will fill the pool)
 *   - 'warning':  projected 100–125% of capacity             (1–25% over)
 *   - 'critical': projected > 125% of capacity               (>25% over)
 *
 * Forecast exhaust date = period_start + (capacity / pace) days,
 * clamped to a valid ISO date. Returns null when pace = 0 (no exhaust)
 * or when the exhaust date falls after period_end (pool survives).
 *
 * "Warn but never block" is the product lock (per task brief): consumers
 * use the returned warning level for visual treatment only. Submit and
 * checkout actions stay enabled regardless of level. The widget's
 * Settings link is the only call-to-action the warning surfaces.
 */

/**
 * Forecast snapshot. All cents fields are integers; date strings are
 * YYYY-MM-DD. `forecastExhaustDate` is null when no exhaust is
 * predicted (pace = 0 or capacity outlasts the period).
 */
export interface BurnRateForecast {
  /** Days from period_start to today (inclusive of today). 0 on day 1. */
  daysIntoPeriod: number;
  /** Days from today to period_end (inclusive of period_end). 0 on the
   *  final day; never negative for active periods (the hook returns the
   *  empty-state forecast once the period has ended). */
  daysRemainingInPeriod: number;
  /** SUM of non-refunded orders.budget_cents in the period. Mirrors the
   *  widget's "Spent" stat. */
  spentCents: number;
  /** Allocated + carried_over + adjustments (signed). Capacity, not
   *  headline pool size, so the percent math matches actual budget. */
  allocatedCents: number;
  /** spent / daysIntoPeriod, rounded to integer cents. 0 when
   *  daysIntoPeriod = 0 (period just started) — pace can't be computed
   *  before any time has elapsed. */
  pacePerDayCents: number;
  /** pace × totalPeriodDays. The "if you keep going at the same rate"
   *  end-of-period number. */
  projectedSpendCents: number;
  /** Estimated date the pool runs out at current pace. YYYY-MM-DD.
   *  null when pace = 0 (no exhaust) or capacity outlasts the period. */
  forecastExhaustDate: string | null;
  /** max(0, projected − capacity). Always ≥ 0; the level encodes
   *  whether the buyer is forecast to overrun at all. */
  overrunCents: number;
  /** True iff projected > capacity. Useful as a quick boolean in the
   *  consumer; level encodes the severity tier. */
  isOverrunForecast: boolean;
  /** Banner severity per the threshold table in the file header. */
  warningLevel: "none" | "caution" | "warning" | "critical";
}

/** Empty/null forecast used when inputs are not ready or the period
 *  doesn't exist. Surfaces zeros + 'none' so consumers don't need a
 *  separate "is loading" guard for the warning row. */
const EMPTY_FORECAST: BurnRateForecast = {
  daysIntoPeriod: 0,
  daysRemainingInPeriod: 0,
  spentCents: 0,
  allocatedCents: 0,
  pacePerDayCents: 0,
  projectedSpendCents: 0,
  forecastExhaustDate: null,
  overrunCents: 0,
  isOverrunForecast: false,
  warningLevel: "none",
};

/**
 * Pure forecast computation. Exported so tests can pin the math
 * without the React Query roundtrip. Today's date is injected
 * explicitly (rather than reading new Date()) so tests get
 * deterministic boundaries without fake-timer plumbing for every case.
 */
export function computeBurnRateForecast(args: {
  period: Pick<
    CompanyBudgetPeriod,
    "period_start" | "period_end" | "allocated_cents" | "carried_over_cents"
  > | null;
  totals: Pick<
    BudgetPeriodTotals,
    "spentCents" | "adjustmentsCents"
  > | null;
  today: Date;
}): BurnRateForecast {
  const { period, totals, today } = args;
  if (!period || !totals) return EMPTY_FORECAST;

  const periodStart = parseISO(period.period_start);
  const periodEnd = parseISO(period.period_end);
  // Day 1 of the period: today === period_start ⇒ daysIntoPeriod = 1.
  // (differenceInCalendarDays returns 0 for same-day; we add 1 so pace
  // math doesn't divide by zero on the first day.) Negative values are
  // clamped — should never happen for an active period the widget
  // renders, but defensive in case a clock skew shows today as before
  // period_start.
  const rawDaysInto = differenceInCalendarDays(today, periodStart) + 1;
  const daysIntoPeriod = Math.max(0, rawDaysInto);
  // Days remaining is calendar diff from today to period_end. 0 on the
  // final day. Clamped at 0 — once the period is over, we surface
  // 'none' below and the remaining value is informational only.
  const rawDaysRemaining = differenceInCalendarDays(periodEnd, today);
  const daysRemainingInPeriod = Math.max(0, rawDaysRemaining);
  const totalPeriodDays = daysIntoPeriod + daysRemainingInPeriod;

  const spentCents = Math.max(0, totals.spentCents);
  const allocatedCents = Math.max(
    0,
    period.allocated_cents +
      period.carried_over_cents +
      totals.adjustmentsCents,
  );

  // Pace: integer cents per day. Floor to avoid floating-point bleed
  // into the projected total. When daysIntoPeriod < 1 (boundary case),
  // pace is 0 — the data-too-sparse guard below will return 'none'
  // before any consumer uses the projection.
  const pacePerDayCents =
    daysIntoPeriod >= 1 ? Math.floor(spentCents / daysIntoPeriod) : 0;
  const projectedSpendCents = pacePerDayCents * totalPeriodDays;

  const overrunRaw = projectedSpendCents - allocatedCents;
  const overrunCents = Math.max(0, overrunRaw);
  const isOverrunForecast = overrunRaw > 0;

  // Exhaust date: period_start + (capacity / pace) days. Only meaningful
  // when pace > 0 AND the exhaust falls inside the period — if capacity
  // outlasts the period at current pace, we return null (no exhaust).
  //
  // Build the date in pure-UTC components to avoid the date-fns/parseISO
  // local-time bleed: parseISO("2026-05-01") returns a Date whose UTC
  // components depend on the runner's timezone, and setUTCDate would
  // then operate on a UTC midnight that's off by one day in negative
  // offsets. Constructing via Date.UTC sidesteps that.
  let forecastExhaustDate: string | null = null;
  if (pacePerDayCents > 0 && allocatedCents > 0) {
    const daysUntilExhaust = Math.ceil(allocatedCents / pacePerDayCents);
    if (daysUntilExhaust <= totalPeriodDays) {
      const [psYear, psMonth, psDay] = period.period_start
        .split("-")
        .map((s) => Number.parseInt(s, 10));
      // daysUntilExhaust counts day 1 as the first day of pace; so
      // exhaust date = period_start + (daysUntilExhaust - 1) calendar
      // days (the day on which cumulative spend crosses capacity).
      const exhaust = new Date(
        Date.UTC(psYear, psMonth - 1, psDay + daysUntilExhaust - 1),
      );
      forecastExhaustDate = exhaust.toISOString().split("T")[0];
    }
  }

  // Warning level — confidence guards first, then severity bands.
  const periodHasEnded = daysRemainingInPeriod === 0 && rawDaysRemaining < 0;
  const dataTooSparse = daysIntoPeriod < 3;
  const noSpend = spentCents === 0;

  let warningLevel: BurnRateForecast["warningLevel"] = "none";
  if (
    !periodHasEnded &&
    !dataTooSparse &&
    !noSpend &&
    allocatedCents > 0
  ) {
    const ratio = projectedSpendCents / allocatedCents;
    if (ratio > 1.25) warningLevel = "critical";
    else if (ratio > 1.0) warningLevel = "warning";
    else if (ratio >= 0.9) warningLevel = "caution";
    else warningLevel = "none";
  }

  return {
    daysIntoPeriod,
    daysRemainingInPeriod,
    spentCents,
    allocatedCents,
    pacePerDayCents,
    projectedSpendCents,
    forecastExhaustDate,
    overrunCents,
    isOverrunForecast,
    warningLevel,
  };
}

/**
 * Hook wrapper. Reads the active period + totals via the existing
 * MS556 hooks and memoises the forecast against their data. Returns
 * the empty forecast while the underlying queries are loading or when
 * the period query resolves to null (no active period) — consumers
 * always get a typed object, never undefined, so the widget can render
 * an unchanging "loading" frame without conditional rendering on every
 * field access.
 *
 * The companyId is optional because the widget passes null when it
 * has already decided to hide (per-celebration cadence or anon view);
 * the underlying queries are gated by useQuery's `enabled` flag.
 */
export function useBurnRateForecast(
  companyId: string | null | undefined,
): BurnRateForecast {
  const periodQuery = useCurrentBudgetPeriod(companyId);
  const period = periodQuery.data ?? null;
  const totalsQuery = useBudgetSpentInPeriod(
    companyId,
    period?.id ?? null,
    period?.period_start ?? null,
    period?.period_end ?? null,
  );
  const totals = totalsQuery.data ?? null;

  return useMemo(
    () =>
      computeBurnRateForecast({
        period,
        totals,
        today: new Date(),
      }),
    [period, totals],
  );
}
